import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const HeroSection = styled.section`
  height: 50vh;
  min-height: 400px;
  background: linear-gradient(rgba(26, 42, 58, 0.5), rgba(26, 42, 58, 0.6)),
              url('https://images.unsplash.com/photo-1615655114865-4cc1bda5901e?w=1920&q=80');
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  margin-bottom: 60px;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(196, 164, 132, 0.2) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const HeroTitle = styled.h1`
  font-size: 64px;
  margin-bottom: 20px;
  letter-spacing: 3px;
  font-weight: 300;
  position: relative;
  z-index: 1;
  
  span {
    color: #c4a484;
    font-weight: 400;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.8;
  opacity: 0.9;
  position: relative;
  z-index: 1;
`;

const SectionTitle = styled.h2`
  font-size: 42px;
  text-align: center;
  margin: 60px 0 40px;
  color: #1a2a3a;
  font-weight: 400;
  position: relative;
  
  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #c4a484, #8a6d4b);
    margin: 20px auto 0;
    border-radius: 2px;
  }
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin: 50px 0;
`;

const CategoryCard = styled(Link)`
  background: linear-gradient(135deg, #1a2a3a 0%, #2c3e50 100%);
  border-radius: 12px;
  padding: 35px 25px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(26, 42, 58, 0.15);
  transition: all 0.3s ease;
  text-decoration: none;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #c4a484, #d4b896);
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(26, 42, 58, 0.25);
  }
`;

const CategoryIcon = styled.div`
  width: 60px;
  height: 60px;
  margin: 0 auto 20px;
  background: rgba(196, 164, 132, 0.15);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 28px;
    height: 28px;
    fill: #c4a484;
  }
`;

const CategoryName = styled.h3`
  font-size: 18px;
  color: white;
  margin-bottom: 8px;
  font-weight: 500;
  letter-spacing: 0.5px;
`;

const CategoryCount = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const GemTable = styled.div`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  margin: 60px 0;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  background: #1a2a3a;
  color: white;
  padding: 15px 20px;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: 1px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  padding: 15px 20px;
  border-bottom: 1px solid #f0f0f0;
  align-items: center;
  transition: background 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
`;

const GemName = styled.span`
  font-weight: 500;
  color: #1a2a3a;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const GemCategory = styled.span`
  color: #666;
  font-size: 14px;
`;

const GemPrice = styled.span`
  color: #c4a484;
  font-weight: 500;
`;

const GemOrigin = styled.span`
  color: #666;
  font-size: 14px;
`;

const SpecialTag = styled.span`
  background: ${props => {
    if (props.tag === 'SALE') return '#f39c12';
    if (props.tag === 'NEW') return '#2ecc71';
    if (props.tag === 'HOT') return '#ff4757';
    if (props.tag === 'RARE') return '#9b59b6';
    return '#c4a484';
  }};
  color: white;
  font-size: 10px;
  padding: 3px 8px;
  border-radius: 12px;
  text-transform: uppercase;
  font-weight: 600;
  white-space: nowrap;
`;

// Diamond icon SVG
const DiamondIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L2 7l10 13 10-13-10-5zM4.5 8.5l7.5-3 7.5 3L12 18l-7.5-9.5z"/>
    <path d="M12 5.5L6 9v6l6 4 6-4V9l-6-3.5z"/>
    <path d="M12 12l-3-2v4l3 2 3-2v-4l-3 2z"/>
  </svg>
);

// Sparkle icon SVG
const SparkleIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0L14 10L24 12L14 14L12 24L10 14L0 12L10 10L12 0z"/>
  </svg>
);

// Gem icon SVG
const GemIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L4 7v10l8 5 8-5V7l-8-5zm0 2.5L17 8l-5 3.5L7 8l5-3.5zM6 9.5l5 3v6l-5 3-5-3v-6l5-3zm12 0l-5 3v6l5 3 5-3v-6l-5-3z"/>
  </svg>
);

// Garnet icon SVG
const GarnetIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 2a10 10 0 0 1 0 20" fill="rgba(255,255,255,0.2)"/>
  </svg>
);

// Star icon SVG
const StarIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0l3 7h7l-5.5 4.5 2 7.5-6.5-4.5-6.5 4.5 2-7.5L2 7h7l3-7z"/>
  </svg>
);

// Tag icon SVG
const TagIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
    <line x1="7" y1="7" x2="7.01" y2="7" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const ViewAll = () => {
  const categories = [
    { name: "Precious Gems", count: 12, icon: <DiamondIcon />, link: "/precious-gems" },
    { name: "Fine Gems", count: 18, icon: <SparkleIcon />, link: "/fine-gems" },
    { name: "Tourmaline", count: 9, icon: <GemIcon />, link: "/tourmaline" },
    { name: "Garnets", count: 7, icon: <GarnetIcon />, link: "/garnets" },
    { name: "New Arrivals", count: 5, icon: <StarIcon />, link: "/new-arrivals" },
    { name: "On Sale", count: 4, icon: <TagIcon />, link: "/on-sale" }
  ];

  const allGems = [
    { name: "Pigeon-Blood Ruby", category: "Precious", price: "$15,000+", origin: "High-mountain deposits", tag: "HOT" },
    { name: "Renowned Emerald", category: "Precious", price: "$12,000+", origin: "Renowned historic mines", tag: "" },
    { name: "Kashmir-Style Sapphire", category: "Precious", price: "$20,000+", origin: "Highland deposits", tag: "RARE" },
    { name: "Neon Tourmaline", category: "Tourmaline", price: "$25,000+", origin: "Coastal pegmatites", tag: "NEW" },
    { name: "Demantoid Garnet", category: "Garnet", price: "$8,000+", origin: "Northern metamorphic belts", tag: "RARE" },
    { name: "Tanzanite-Style Gem", category: "Fine", price: "$2,000+", origin: "Highland alluvial deposits", tag: "NEW" },
    { name: "Blue Zircon", category: "Fine", price: "$1,500+", origin: "Ancient volcanic deposits", tag: "HOT" },
    { name: "Alexandrite", category: "Fine", price: "$10,000+", origin: "Northern metamorphic belts", tag: "RARE" },
    { name: "Tsavorite Garnet", category: "Garnet", price: "$5,000+", origin: "Forest alluvial deposits", tag: "" },
    { name: "Rubellite Tourmaline", category: "Tourmaline", price: "$6,000+", origin: "Remote mountain deposits", tag: "" }
  ];

  return (
    <PageContainer>
      <HeroSection>
        <div>
          <HeroTitle>
            <span>Complete</span> Collection
          </HeroTitle>
          <HeroSubtitle>
            Browse our entire catalog • Every gem • Every category
          </HeroSubtitle>
        </div>
      </HeroSection>

      <CategoryGrid>
        {categories.map((cat, index) => (
          <CategoryCard key={index} to={cat.link}>
            <CategoryIcon>{cat.icon}</CategoryIcon>
            <CategoryName>{cat.name}</CategoryName>
            <CategoryCount>{cat.count} gems</CategoryCount>
          </CategoryCard>
        ))}
      </CategoryGrid>

      <SectionTitle>All Gemstones</SectionTitle>
      <GemTable>
        <TableHeader>
          <span>Gemstone</span>
          <span>Category</span>
          <span>Price Range</span>
          <span>Origin</span>
        </TableHeader>
        {allGems.map((gem, index) => (
          <TableRow key={index}>
            <GemName>
              {gem.name}
              {gem.tag && <SpecialTag tag={gem.tag}>{gem.tag}</SpecialTag>}
            </GemName>
            <GemCategory>{gem.category}</GemCategory>
            <GemPrice>{gem.price}</GemPrice>
            <GemOrigin>{gem.origin}</GemOrigin>
          </TableRow>
        ))}
      </GemTable>
    </PageContainer>
  );
};

export default ViewAll;
